'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { ArrowRightIcon, GlobeAltIcon } from '@heroicons/react/24/outline'
import { Modal } from '@/Modal'
import { Button } from '@/General/Button'
import { getCountryFlag } from '@/utils/helpers'
import { useCookie } from '@/Cookies/useCookies'
import { isEuropeanCountry } from '@/lib/middleware/variantRedirect.helper'
import {
  navigateToMarketForBrand,
  sanitizeRedirectPath,
} from '@/lib/auth/domainSwitch'
import {
  inferRegionalBrandFromHostname,
  type RegionalBrandCode,
} from '@/lib/seo/marketUrls'
import { useStore } from '@/store'
import { shouldOfferWrongStorefrontRegionModal } from '@/lib/storefront/wrongStorefrontRegionModalRoutes'

const DISMISS_COOKIE_NAME = 'vi-wrong-storefront-modal-dismissed'
const DISMISS_MAX_AGE_SECONDS = 60 * 60 * 8
const GEO_LOOKUP_CACHE_KEY = 'vi-wrong-storefront-geo'
const GEO_LOOKUP_CACHE_TTL_MS = 60 * 30 * 1000

/** Dev-only: `?viSimulateCountry=US` (ISO2) to mimic geo when Vercel headers are missing. */
const SIMULATE_PARAM = 'viSimulateCountry'

type CachedGeoLookup = {
  country: string | null
  expiresAt: number
}

function readCachedGeoLookup(): CachedGeoLookup | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(GEO_LOOKUP_CACHE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<CachedGeoLookup>
    const country = parsed.country?.trim().toUpperCase() || null
    const expiresAt =
      typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0

    if (expiresAt <= Date.now()) {
      window.localStorage.removeItem(GEO_LOOKUP_CACHE_KEY)
      return null
    }

    if (country && !/^[A-Z]{2}$/.test(country)) return null

    return { country, expiresAt }
  } catch {
    return null
  }
}

function writeCachedGeoLookup(country: string | null): void {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      GEO_LOOKUP_CACHE_KEY,
      JSON.stringify({
        country,
        expiresAt: Date.now() + GEO_LOOKUP_CACHE_TTL_MS,
      } satisfies CachedGeoLookup)
    )
  } catch {
    // Ignore storage failures; the modal can still fall back to /api/geo.
  }
}

function parseSimulatedCountryFromSearch(): string | null {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'production') {
    return null
  }
  try {
    const raw = new URLSearchParams(window.location.search).get(SIMULATE_PARAM)
    const code = raw?.trim().toUpperCase()
    return code && /^[A-Z]{2}$/.test(code) ? code : null
  } catch {
    return null
  }
}

function pathWithoutSimulateParam(pathname: string, search: string): string {
  try {
    const u = new URL(pathname + search, 'https://placeholder.invalid')
    if (!u.searchParams.has(SIMULATE_PARAM)) {
      return `${pathname}${search}`
    }
    u.searchParams.delete(SIMULATE_PARAM)
    const q = u.searchParams.toString()
    return `${u.pathname}${q ? `?${q}` : ''}`
  } catch {
    return `${pathname}${search}`
  }
}

export function WrongStorefrontRegionModal() {
  const pathname = usePathname()
  const { getCookie, setCookie } = useCookie()
  const pathAllowed = useMemo(
    () => shouldOfferWrongStorefrontRegionModal(pathname ?? '/'),
    [pathname]
  )

  const [isOpen, setIsOpen] = useState(false)
  const [visitorCountry, setVisitorCountry] = useState<string | null>(null)
  const [isNavigating, setIsNavigating] = useState(false)
  const shoppingCartId = useStore(state => state.shoppingCartId)
  const isDismissed = useCallback(
    () => getCookie(DISMISS_COOKIE_NAME) === '1',
    [getCookie]
  )
  const persistDismiss = useCallback(
    () => setCookie(DISMISS_COOKIE_NAME, '1', DISMISS_MAX_AGE_SECONDS),
    [setCookie]
  )

  const suggestedBrand: RegionalBrandCode | null = useMemo(() => {
    if (typeof window === 'undefined' || !visitorCountry) return null
    const siteBrand = inferRegionalBrandFromHostname(window.location.host)
    if (!siteBrand) return null

    const europe = isEuropeanCountry(visitorCountry)

    if (siteBrand === 'VANISHING_INC_UK' && !europe) {
      return 'VANISHING_INC_US'
    }
    if (siteBrand === 'VANISHING_INC_US' && europe) {
      return 'VANISHING_INC_UK'
    }
    return null
  }, [visitorCountry])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (!pathAllowed) return

      const simulated = parseSimulatedCountryFromSearch()
      if (simulated) {
        setVisitorCountry(simulated)
        return
      }

      if (isDismissed()) return

      const cached = readCachedGeoLookup()
      if (cached) {
        if (cached.country) {
          setVisitorCountry(cached.country)
        }
        return
      }

      try {
        const res = await fetch('/api/geo')
        if (!res.ok || cancelled) return
        const data = (await res.json()) as { country?: string | null }
        const code = data.country?.trim().toUpperCase() || null
        writeCachedGeoLookup(code)
        if (!code || cancelled) return
        setVisitorCountry(code)
      } catch {
        // ignore — no modal without geo
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [pathAllowed, isDismissed])

  useEffect(() => {
    queueMicrotask(() => {
      if (!pathAllowed) {
        setIsOpen(false)
        return
      }
      if (isDismissed()) {
        setIsOpen(false)
        return
      }
      if (!suggestedBrand) return
      setIsOpen(true)
    })
  }, [suggestedBrand, pathAllowed, isDismissed])

  const handleClose = useCallback(() => {
    persistDismiss()
    setIsOpen(false)
  }, [persistDismiss])

  const siteBrand = useMemo(() => {
    if (typeof window === 'undefined') return null
    return inferRegionalBrandFromHostname(window.location.host)
  }, [])

  const copy = useMemo(() => {
    if (!suggestedBrand) {
      return { headline: '', supporting: '' }
    }
    if (suggestedBrand === 'VANISHING_INC_US') {
      return {
        headline: 'Faster shipping from our US warehouse',
        supporting:
          'We ship from the US and the UK. For your address, our US store usually gets your order out the door quickest—same catalog, same team.',
      }
    }
    return {
      headline: 'Faster shipping from our UK warehouse',
      supporting:
        'We ship from the UK and the US. For your region, our UK store is typically the fastest option—with pricing that works for European orders.',
    }
  }, [suggestedBrand])

  const primaryCtaLabel = useMemo(() => {
    if (!suggestedBrand) return ''
    return suggestedBrand === 'VANISHING_INC_UK'
      ? 'Shop Vanishing Inc. UK'
      : 'Shop Vanishing Inc. USA'
  }, [suggestedBrand])

  const handleContinue = useCallback(async () => {
    if (!suggestedBrand || !visitorCountry) return
    const cleanPath = pathWithoutSimulateParam(
      window.location.pathname,
      window.location.search
    )
    const redirectPath = sanitizeRedirectPath(cleanPath, '/')
    setIsNavigating(true)
    try {
      await navigateToMarketForBrand({
        brandCode: suggestedBrand,
        redirectPath,
        selectedCountryIsoCode: visitorCountry,
        shoppingCartToken: shoppingCartId,
      })
    } finally {
      setIsNavigating(false)
    }
  }, [suggestedBrand, visitorCountry, shoppingCartId])

  if (!suggestedBrand) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnBackdropClick
      backdropOpacity="subtle"
    >
      <div className="relative">
        <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 px-6 pt-8 pb-6 overflow-hidden">
          <div className="flex flex-col items-center">
            {siteBrand && suggestedBrand ? (
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500 shadow-lg">
                    <span className="text-3xl leading-none">
                      {getCountryFlag(null, siteBrand)}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-gray-600">
                    This site
                  </span>
                </div>
                <ArrowRightIcon className="h-7 w-7 shrink-0 text-green-600" />
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500 shadow-lg">
                    <span className="text-3xl leading-none">
                      {getCountryFlag(null, suggestedBrand)}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-gray-600">
                    Recommended
                  </span>
                </div>
              </div>
            ) : null}
            <div className="mt-5 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {copy.headline}
              </h3>
              {visitorCountry ? (
                <p className="text-green-700 font-medium text-sm sm:text-base">
                  <span className="mr-1.5 inline-block" aria-hidden>
                    {getCountryFlag(visitorCountry)}
                  </span>
                  Shopping from {visitorCountry}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 py-6 space-y-3">
        <p className="text-gray-600 text-base leading-relaxed">
          {copy.supporting}
        </p>
        <Button
          type="button"
          className="w-full"
          onClick={() => void handleContinue()}
          disabled={isNavigating}
        >
          <GlobeAltIcon className="w-5 h-5 mr-2 group-hover:animate-bounce" />
          {isNavigating ? 'Taking you there…' : primaryCtaLabel}
          {!isNavigating ? (
            <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
          ) : null}
        </Button>
        <Button
          type="button"
          color="gray"
          className="w-full"
          onClick={handleClose}
          disabled={isNavigating}
        >
          Stay on this site
        </Button>
      </div>
    </Modal>
  )
}
